import * as PIXI from 'pixi.js'
import { sound } from '@pixi/sound'

/**
 * アセット管理用クラス
 */
class AsseetsManager {
  // ---------------------------------------------------------------------------------------------------------
  // --	VARS
  // ---------------------------------------------------------------------------------------------------------

  //シングルトンインスタンス
  private static _instance: AsseetsManager

  //再生中のサウンドを保持するMAP
  private _playingSounds: Map<any, any> = new Map()

  //ミュートしているかどうかのフラグ
  private _isMute: boolean = false

  // ---------------------------------------------------------------------------------------------------------
  // --	AsseetsManager
  // ---------------------------------------------------------------------------------------------------------

  /**
   * コンストラクタ
   */
  private constructor() {}

  /** インスタンスの取得 */
  public static get instance(): AsseetsManager {
    if (!this._instance) {
      this._instance = new AsseetsManager()
    }

    // 生成済みのインスタンスを返す
    return this._instance
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	読み込み
  // ---------------------------------------------------------------------------------------------------------

  async loadBundle(name, assets) {
    PIXI.Assets.addBundle(name, assets)
    const result = await PIXI.Assets.loadBundle(name)
    console.log(result)
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	画像
  // ---------------------------------------------------------------------------------------------------------

  /**
   * テクスチャ取得
   */
  getTexture(name: string, antialiasing: boolean = false): PIXI.Texture {
    const texture: PIXI.Texture = PIXI.Assets.get(name)
    texture.baseTexture.scaleMode = antialiasing
      ? PIXI.SCALE_MODES.LINEAR
      : PIXI.SCALE_MODES.NEAREST
    return texture
  }

  /**
   * アニメーション取得
   */
  getAnimation(key: string, label: string, antialiasing: boolean = false): Array<PIXI.Texture> {
    const bundle = PIXI.Assets.get(key)
    const animations = bundle ? bundle.animations : null
    const frames = animations ? animations[label] : null

    return frames
  }

  /**
   * スプライト生成
   */
  createSpriteByName(name: string, antialiasing: boolean = false): PIXI.Sprite {
    return PIXI.Sprite.from(this.getTexture(name, antialiasing))
  }

  /**
   * アニメーション作成
   */
  createMovieClipFromAnimation(
    key: string,
    label: string,
    antialiasing: boolean = false
  ): PIXI.AnimatedSprite {
    const frames: Array<PIXI.Texture> = this.getAnimation(key, label)
    frames.forEach(
      (frame) =>
        (frame.baseTexture.scaleMode = antialiasing
          ? PIXI.SCALE_MODES.LINEAR
          : PIXI.SCALE_MODES.NEAREST)
    )

    if (frames) {
      return this.createMovieClip(frames)
    } else {
      throw new Error('指定されたラベル名のアニメーションがありません')
    }
  }

  /**
   * ムービークリップ生成
   */
  createMovieClip(frames: Array<PIXI.Texture>): PIXI.AnimatedSprite {
    return new PIXI.AnimatedSprite(frames)
  }

  // ---------------------------------------------------------------------------------------------------------
  // --	サウンド
  // ---------------------------------------------------------------------------------------------------------

  playSound(name: string, option: any = null, callback: Function = null): void {
    const s = PIXI.Assets.get(name)
    if (!s) {
      console.log(name + 'が見つかりませんでした')
      return
    }

    //同じ音はかぶらないようにする
    this.stopSound(name)

    if (!option) {
      option = { volume: 0.1 }
    }

    //再生
    const instance = s.play(option)
    this._playingSounds.set(s, instance)
    instance.on('end', () => {
      this.stopSound(name)
      if (callback) callback()
    })
  }

  stopSound(name: string): void {
    const s = PIXI.Assets.get(name)
    if (!s) {
      console.log(name + 'が見つかりませんでした')
      return
    }

    if (this._playingSounds.has(s)) {
      const instance = this._playingSounds.get(s)
      instance.off('end')
      instance.stop()
      this._playingSounds.delete(s)
    }
  }

  stopAllSounds(): void {
    //全ての音をとめる
    sound.stopAll()
  }

  stop__playingSounds(): void {
    this._playingSounds.forEach((instance, s) => {
      instance.off('end')
      instance.stop()
      this._playingSounds.delete(s)
    })
  }

  soundMute(bool: boolean): void {
    this._isMute = bool

    if (this._isMute) {
      sound.muteAll()
    } else {
      sound.unmuteAll()
    }
  }

  // ---------------------------------------------------------------------------------------------------------
}

export default AsseetsManager
